// Read-only measurements of how well this browser is coping: the link and the machine. One definition,
// shared by the slow-connection notice and Aria's diagnostics, so the two can never disagree.

import { ConnectionSensitiveArea, NetworkQuality } from 'pure-js/types/AppAssistant'

/** Above this round trip, a call or the game starts to feel bad even when pages still load. */
export const SLOW_NETWORK_ROUND_TRIP_MS = 400 // 400 ms

/** Network Information effective types that are slow whatever the measured round trip says. */
export const SLOW_EFFECTIVE_TYPES = ['slow-2g', '2g']

/** The probe is a HEAD of an asset we already serve, so it costs the user nothing extra to fetch. */
const ROUND_TRIP_PROBE_PATH = '/favicon.ico'

/** Long enough to survive a slow-but-usable link, short enough that a dead link resolves quickly. */
const ROUND_TRIP_PROBE_TIMEOUT_MS = 5000 // 5 s

/** One notice per browser tab session, wherever it was raised — it is never said twice in a visit. */
const SLOW_NETWORK_NOTICE_SEEN_KEY = 'intraction_slow_network_notice_seen'

/** Route fragments that make a page depend on a steady link, longest-lived first. */
const CONNECTION_SENSITIVE_ROUTE_FRAGMENTS: [string, ConnectionSensitiveArea][] = [
  ['/play-game', ConnectionSensitiveArea.GAME],
  ['/ai-coach', ConnectionSensitiveArea.AI_DEBRIEF],
  ['/upcoming-activities', ConnectionSensitiveArea.UPCOMING_ACTIVITIES],
  ['/coach', ConnectionSensitiveArea.COACH]
]

/**
 * Which area a page belongs to. Matched on a fragment rather than a full route, so the gamer and
 * facilitator copies of each page — and their nested routes — answer the same.
 *
 * This decides when the link is worth re-checking, NOT how often the user is told: the notice itself is
 * once per visit, whichever page happened to raise it.
 */
export function resolveConnectionSensitiveArea(pathname: string): ConnectionSensitiveArea {
  const match = CONNECTION_SENSITIVE_ROUTE_FRAGMENTS.find(([fragment]) => pathname.includes(fragment))
  return match ? match[1] : ConnectionSensitiveArea.GENERAL
}

export type NetworkQualitySample = {
  quality: NetworkQuality
  /** Round trip in milliseconds, when either the browser or our own probe could produce one. */
  roundTripMs?: number
  /** The browser's own coarse label (`4g`, `3g`, …), when the Network Information API is present. */
  effectiveType?: string
  downlinkMbps?: number
}

type NetworkInformation = {
  effectiveType?: string
  rtt?: number
  downlink?: number
  saveData?: boolean
  addEventListener?: (type: string, listener: () => void) => void
  removeEventListener?: (type: string, listener: () => void) => void
}

/** Chromium-only API; absent in Safari and Firefox, which is why the fetch probe below exists. */
export function getNetworkInformation(): NetworkInformation | undefined {
  if (typeof navigator === 'undefined') return undefined
  return (navigator as Navigator & { connection?: NetworkInformation }).connection
}

function isOnline(): boolean {
  return typeof navigator === 'undefined' ? true : navigator.onLine
}

/** Times a tiny same-origin request; returns undefined rather than a wrong number when it cannot. */
async function probeRoundTripMs(): Promise<number | undefined> {
  if (typeof fetch !== 'function' || typeof performance === 'undefined') return undefined

  const controller = typeof AbortController === 'function' ? new AbortController() : undefined
  const timeout = setTimeout(() => controller?.abort(), ROUND_TRIP_PROBE_TIMEOUT_MS)
  const startedAt = performance.now()

  try {
    await fetch(`${ROUND_TRIP_PROBE_PATH}?networkProbe=${startedAt}`, {
      method: 'HEAD',
      cache: 'no-store',
      signal: controller?.signal
    })
    return performance.now() - startedAt
  } catch {
    return undefined
  } finally {
    clearTimeout(timeout)
  }
}

function classify(roundTripMs: number | undefined, effectiveType: string | undefined): NetworkQuality {
  if (effectiveType && SLOW_EFFECTIVE_TYPES.includes(effectiveType)) return NetworkQuality.SLOW
  if (roundTripMs === undefined) return NetworkQuality.UNKNOWN
  return roundTripMs > SLOW_NETWORK_ROUND_TRIP_MS ? NetworkQuality.SLOW : NetworkQuality.GOOD
}

/** Reads the browser's own estimate where it exists, else times one small request. An estimate of
 * exactly 0 is the browser's "no data yet", not a fast link. */
export async function measureNetworkQuality(): Promise<NetworkQualitySample> {
  if (!isOnline()) return { quality: NetworkQuality.OFFLINE }

  const connection = getNetworkInformation()
  const reportedRoundTripMs = connection?.rtt && connection.rtt > 0 ? connection.rtt : undefined
  const roundTripMs = reportedRoundTripMs ?? (await probeRoundTripMs())

  return {
    quality: classify(roundTripMs, connection?.effectiveType),
    roundTripMs,
    effectiveType: connection?.effectiveType,
    downlinkMbps: connection?.downlink
  }
}

/** Past this, timers are visibly late — the tab is fighting other work on this machine. */
export const BUSY_MAIN_THREAD_LAG_MS = 150 // 150 ms

/** Enough ticks to see a pattern, few enough that the whole measurement is over in a moment. */
const MAIN_THREAD_LAG_SAMPLE_COUNT = 5

/** A short interval keeps the measurement honest: a long one hides brief stalls in the average. */
const MAIN_THREAD_LAG_SAMPLE_INTERVAL_MS = 40 // 40 ms

/** How late a timer runs — what a busy machine feels like while the network is fine. Reported as the
 * WORST tick, since an average hides the stalls being complained about. */
export function measureMainThreadLagMs(): Promise<number> {
  return new Promise((resolve) => {
    let worstLagMs = 0
    let remaining = MAIN_THREAD_LAG_SAMPLE_COUNT
    let scheduledAt = performance.now()

    const tick = () => {
      worstLagMs = Math.max(worstLagMs, performance.now() - scheduledAt - MAIN_THREAD_LAG_SAMPLE_INTERVAL_MS)
      remaining -= 1
      if (remaining <= 0) {
        resolve(Math.max(0, Math.round(worstLagMs)))
        return
      }
      scheduledAt = performance.now()
      setTimeout(tick, MAIN_THREAD_LAG_SAMPLE_INTERVAL_MS)
    }

    setTimeout(tick, MAIN_THREAD_LAG_SAMPLE_INTERVAL_MS)
  })
}

export type ClientPerformanceReport = NetworkQualitySample & {
  online: boolean
  mainThreadLagMs: number
  /** True when the machine, not the link, is what is struggling — the "close some tabs" case. */
  isDeviceBusy: boolean
}

/** The whole picture in one read, so advice about a laggy session is never based on half of it. */
export async function measureClientPerformance(): Promise<ClientPerformanceReport> {
  const [network, mainThreadLagMs] = await Promise.all([measureNetworkQuality(), measureMainThreadLagMs()])

  return {
    ...network,
    online: isOnline(),
    mainThreadLagMs,
    isDeviceBusy: mainThreadLagMs > BUSY_MAIN_THREAD_LAG_MS
  }
}

/** Set just before a reload Aria asked for, so the panel is open again on the other side of it. */
const REOPEN_ASSISTANT_AFTER_RELOAD_KEY = 'intraction_reopen_assistant_after_reload'

export function markAssistantReopenAfterReload(): void {
  try {
    sessionStorage.setItem(REOPEN_ASSISTANT_AFTER_RELOAD_KEY, 'true')
  } catch {
    // Losing the flag only costs the reopen; the reload itself still has to happen.
  }
}

/** Read without clearing: the panel needs to know during render, and only the launcher may spend it. */
export function hasAssistantReopenAfterReload(): boolean {
  try {
    return sessionStorage.getItem(REOPEN_ASSISTANT_AFTER_RELOAD_KEY) === 'true'
  } catch {
    return false
  }
}

/** Reads the flag and clears it, so a later reload the user chose for themselves does not reopen. */
export function consumeAssistantReopenAfterReload(): boolean {
  try {
    const shouldReopen = sessionStorage.getItem(REOPEN_ASSISTANT_AFTER_RELOAD_KEY) === 'true'
    sessionStorage.removeItem(REOPEN_ASSISTANT_AFTER_RELOAD_KEY)
    return shouldReopen
  } catch {
    return false
  }
}

export function hasSeenSlowNetworkNotice(): boolean {
  try {
    return sessionStorage.getItem(SLOW_NETWORK_NOTICE_SEEN_KEY) === 'true'
  } catch {
    return false
  }
}

export function markSlowNetworkNoticeSeen(): void {
  try {
    sessionStorage.setItem(SLOW_NETWORK_NOTICE_SEEN_KEY, 'true')
  } catch {
    // A blocked sessionStorage only costs us the once-per-session guarantee; never break the page for it.
  }
}
